// File: frontend/src/components/CVETable.tsx
import React from 'react';
import useSWR from 'swr';
import { Link as RouterLink } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Link,
    Typography,
    Chip,
    CircularProgress,
    Alert,
    Tooltip,
    styled,
    alpha,
    Fade,
    Collapse,
    IconButton,
    Stack
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SecurityIcon from '@mui/icons-material/Security';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// Distinct icons for status - matching CVESearchPage
import WhatshotIcon from '@mui/icons-material/Whatshot';  // Exploited in Wild
import BuildCircleIcon from '@mui/icons-material/BuildCircle';  // Patch Available
import CodeIcon from '@mui/icons-material/Code';  // PoC Available
import BusinessIcon from '@mui/icons-material/Business';
import { cyber } from '../theme';
import { ExploitationBadgeStack, ExploitationSignals } from './ExploitationBadges';

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: theme.spacing(2),
    background: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(cyber.primary, 0.2)}`,
    overflow: 'hidden',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    '& .MuiTableRow-root': {
        background: `linear-gradient(90deg, ${alpha(cyber.primary, 0.15)} 0%, ${alpha(cyber.primaryLight, 0.15)} 100%)`,
    },
    '& .MuiTableCell-root': {
        fontWeight: 700,
        fontSize: '0.9rem',
        color: cyber.primaryLight,
        borderBottom: `2px solid ${alpha(cyber.primary, 0.2)}`,
        padding: theme.spacing(1.5),
    }
}));

const StyledTableBody = styled(TableBody)(({ theme }) => ({
    '& .MuiTableRow-root': {
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            background: alpha(cyber.primary, 0.05),
        },
    },
    '& .MuiTableCell-root': {
        padding: theme.spacing(1.5),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        verticalAlign: 'top',
    }
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
    background: alpha(theme.palette.background.paper, 0.6),
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(cyber.primary, 0.15)}`,
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
    background: alpha(theme.palette.background.paper, 0.6),
    borderRadius: theme.spacing(2),
    border: `1px dashed ${alpha(cyber.primary, 0.3)}`,
    textAlign: 'center',
}));

// Severity colors mapping (calibrated to the dark cyber palette)
const SEVERITY_COLORS: { [key: string]: string } = {
    'CRITICAL': '#ff5c5c',
    'HIGH': cyber.accent.coral,
    'MEDIUM': cyber.accent.amber,
    'LOW': cyber.accent.green,
    'NONE': '#9fa4c0',
    'UNKNOWN': '#9fa4c0'
};

// CVE Interface matching backend response
interface CVEContext {
    exploited_in_wild: boolean;
    patch_available: boolean;
    poc_available: boolean;
    associated_threat_actors: string[];
    associated_malware: string[];
    context_summary: string;
}

interface AffectedProduct {
    vendor: string;
    product: string;
    versions?: string;
}

interface CVE extends ExploitationSignals {
    cve_id: string;
    description: string | null;
    cvss_v3_score: number | null;
    cvss_v3_severity: string | null;
    cvss_v3_vector: string | null;
    cwe_id: string | null;
    cwe_name: string | null;
    affected_products: AffectedProduct[];
    context: CVEContext;
    published_date: string | null;
    references: { url: string; source: string | null }[];
}

interface CVEByArticleResponse {
    article_id: string;
    article_title: string;
    cves: CVE[];
    total: number;
}

// Severity Chip Component
const SeverityChip: React.FC<{ severity: string | null; score: number | null }> = ({ severity, score }) => {
    const sev = severity?.toUpperCase() || 'UNKNOWN';
    const color = SEVERITY_COLORS[sev] || SEVERITY_COLORS['UNKNOWN'];
    
    return (
        <Tooltip title={score ? `CVSS Score: ${score}` : 'No CVSS score available'}>
            <Chip
                label={score ? `${sev} (${score})` : sev}
                size="small"
                sx={{
                    bgcolor: alpha(color, 0.15),
                    color: color,
                    fontWeight: 'bold',
                    border: `1px solid ${alpha(color, 0.3)}`,
                }}
            />
        </Tooltip>
    );
};

// Status Icons Component - Using distinct icons
const StatusIcons: React.FC<{ 
    exploited: boolean; 
    patched: boolean; 
    poc: boolean 
}> = ({ exploited, patched, poc }) => (
    <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip title={exploited ? "🔥 Actively Exploited" : "No active exploitation"}>
            <WhatshotIcon sx={{ fontSize: 20, color: exploited ? 'error.main' : '#555', opacity: exploited ? 1 : 0.4 }} />
        </Tooltip>
        <Tooltip title={patched ? "🔧 Patch Available" : "No patch yet"}>
            <BuildCircleIcon sx={{ fontSize: 20, color: patched ? cyber.accent.green : '#555', opacity: patched ? 1 : 0.4 }} />
        </Tooltip>
        <Tooltip title={poc ? "💻 PoC Available" : "No public PoC"}>
            <CodeIcon sx={{ fontSize: 20, color: poc ? cyber.accent.amber : '#555', opacity: poc ? 1 : 0.4 }} />
        </Tooltip>
    </Stack>
);

// Expandable Row Component
const CVERow: React.FC<{ cve: CVE }> = ({ cve }) => {
    const [expanded, setExpanded] = React.useState(false);

    return (
        <>
            <TableRow>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Link
                            href={`https://nvd.nist.gov/vuln/detail/${cve.cve_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                fontWeight: 'bold',
                                color: alpha(cyber.accent.cyan, 0.9),
                                textDecorationColor: alpha(cyber.accent.cyan, 0.4),
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontFamily: cyber.fontMono,
                                fontSize: '0.8rem',
                            }}
                        >
                            {cve.cve_id}
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                    </Box>
                </TableCell>
                <TableCell>
                    <SeverityChip severity={cve.cvss_v3_severity} score={cve.cvss_v3_score} />
                </TableCell>
                <TableCell>
                    <ExploitationBadgeStack signals={cve} />
                    {!cve.in_cisa_kev && (cve.epss_score === null || cve.epss_score === undefined) && (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                </TableCell>
                <TableCell>
                    <StatusIcons
                        exploited={cve.context?.exploited_in_wild || false}
                        patched={cve.context?.patch_available || false}
                        poc={cve.context?.poc_available || false}
                    />
                </TableCell>
                <TableCell>
                    {cve.affected_products?.slice(0, 2).map((prod, idx) => (
                        <Chip
                            key={idx}
                            icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                            label={`${prod.vendor}/${prod.product}`}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
                        />
                    ))}
                    {cve.affected_products?.length > 2 && (
                        <Chip label={`+${cve.affected_products.length - 2}`} size="small" sx={{ fontSize: '0.7rem' }} />
                    )}
                </TableCell>
                <TableCell>
                    <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: '0.85rem' }}>
                        {cve.context?.context_summary || cve.description || 'No context available'}
                    </Typography>
                </TableCell>
            </TableRow>
            
            {/* Expanded Row */}
            <TableRow>
                <TableCell colSpan={6} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 1 }}>
                            {cve.description && (
                                <Paper sx={{ p: 2, mb: 2, bgcolor: alpha(cyber.primary, 0.05), border: `1px solid ${alpha(cyber.primary, 0.15)}` }}>
                                    <Typography variant="subtitle2" sx={{ color: cyber.primaryLight }} gutterBottom>Description</Typography>
                                    <Typography variant="body2">{cve.description}</Typography>
                                </Paper>
                            )}

                            {cve.context?.context_summary && cve.context.context_summary !== cve.description && (
                                <Paper sx={{ p: 2, mb: 2, bgcolor: alpha(cyber.accent.amber, 0.06), border: `1px solid ${alpha(cyber.accent.amber, 0.2)}` }}>
                                    <Typography variant="subtitle2" color="warning.main" gutterBottom>Threat Context</Typography>
                                    <Typography variant="body2">{cve.context.context_summary}</Typography>
                                </Paper>
                            )}

                            {/* Status Legend */}
                            <Paper sx={{ p: 1.5, mb: 2, bgcolor: alpha(cyber.primary, 0.04) }}>
                                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <WhatshotIcon sx={{ fontSize: 18, color: cve.context?.exploited_in_wild ? 'error.main' : '#555' }} />
                                        <Typography variant="caption" color={cve.context?.exploited_in_wild ? 'error' : 'text.secondary'}>
                                            {cve.context?.exploited_in_wild ? 'Actively Exploited' : 'No Active Exploitation'}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <BuildCircleIcon sx={{ fontSize: 18, color: cve.context?.patch_available ? cyber.accent.green : '#555' }} />
                                        <Typography variant="caption" color={cve.context?.patch_available ? 'success.main' : 'text.secondary'}>
                                            {cve.context?.patch_available ? 'Patch Available' : 'No Patch Yet'}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <CodeIcon sx={{ fontSize: 18, color: cve.context?.poc_available ? cyber.accent.amber : '#555' }} />
                                        <Typography variant="caption" color={cve.context?.poc_available ? 'warning.main' : 'text.secondary'}>
                                            {cve.context?.poc_available ? 'PoC Available' : 'No Public PoC'}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Paper>

                            {cve.context?.associated_threat_actors?.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ color: cyber.primaryLight }} gutterBottom>Associated Threat Actors</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {cve.context.associated_threat_actors.map((actor, idx) => (
                                            <Tooltip key={idx} title="Search this actor in Threat Entities">
                                                <Chip
                                                    component={RouterLink}
                                                    to={`/entities?q=${encodeURIComponent(actor)}`}
                                                    clickable
                                                    label={actor}
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                />
                                            </Tooltip>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {cve.context?.associated_malware?.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ color: cyber.primaryLight }} gutterBottom>Associated Malware</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {cve.context.associated_malware.map((malware, idx) => (
                                            <Tooltip key={idx} title="Search this malware in Threat Entities">
                                                <Chip
                                                    component={RouterLink}
                                                    to={`/entities?q=${encodeURIComponent(malware)}`}
                                                    clickable
                                                    label={malware}
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                />
                                            </Tooltip>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {cve.in_cisa_kev && (
                                <Paper sx={{ p: 2, mb: 2, bgcolor: alpha('#d32f2f', 0.06), border: `1px solid ${alpha('#d32f2f', 0.25)}` }}>
                                    <Typography variant="subtitle2" sx={{ color: '#d32f2f' }} gutterBottom>
                                        CISA Known Exploited Vulnerability
                                    </Typography>
                                    <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                                        {cve.kev_date_added && (
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Added to KEV</Typography>
                                                <Typography variant="body2">{cve.kev_date_added}</Typography>
                                            </Box>
                                        )}
                                        {cve.kev_due_date && (
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Remediation Due</Typography>
                                                <Typography variant="body2" fontWeight="bold">{cve.kev_due_date}</Typography>
                                            </Box>
                                        )}
                                        {cve.kev_ransomware_use && (
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Ransomware</Typography>
                                                <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>Known campaign use</Typography>
                                            </Box>
                                        )}
                                        {cve.epss_score !== null && cve.epss_score !== undefined && (
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">EPSS</Typography>
                                                <Typography variant="body2">{(cve.epss_score * 100).toFixed(2)}% exploitation probability</Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </Paper>
                            )}

                            <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                                {cve.cwe_id && (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: cyber.primaryLight }} gutterBottom>CWE</Typography>
                                        <Link
                                            href={`https://cwe.mitre.org/data/definitions/${cve.cwe_id.replace('CWE-', '')}.html`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ fontFamily: cyber.fontMono, fontSize: '0.8rem', color: alpha(cyber.accent.cyan, 0.9), textDecorationColor: alpha(cyber.accent.cyan, 0.4) }}
                                        >
                                            {cve.cwe_id}{cve.cwe_name ? `: ${cve.cwe_name}` : ''}
                                        </Link>
                                    </Box>
                                )}
                                {cve.cvss_v3_vector && (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: cyber.primaryLight }} gutterBottom>CVSS Vector</Typography>
                                        <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, fontSize: '0.75rem', color: alpha(cyber.accent.cyan, 0.85) }}>{cve.cvss_v3_vector}</Typography>
                                    </Box>
                                )}
                                {cve.published_date && (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: cyber.primaryLight }} gutterBottom>Published</Typography>
                                        <Typography variant="body2">{new Date(cve.published_date).toLocaleDateString()}</Typography>
                                    </Box>
                                )}
                            </Stack>

                            {cve.references?.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: cyber.primaryLight }} gutterBottom>References</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {cve.references.slice(0, 5).map((ref, idx) => (
                                            <Link key={idx} href={ref.url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {ref.source || (() => { try { return new URL(ref.url).hostname; } catch { return 'Link'; }})()}
                                                <OpenInNewIcon sx={{ fontSize: 12 }} />
                                            </Link>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

// Main Component
interface CVETableProps {
    reportId: string;
}

const CVETable: React.FC<CVETableProps> = ({ reportId }) => {
    const { authenticatedFetcher } = useApi();
    
    const { data, error, isLoading } = useSWR<CVEByArticleResponse>(
        reportId ? `/api/cves/by-article/${reportId}` : null,
        authenticatedFetcher
    );

    if (isLoading) {
        return (
            <Fade in timeout={500}>
                <LoadingContainer>
                    <CircularProgress size={48} thickness={4} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading CVE data...</Typography>
                </LoadingContainer>
            </Fade>
        );
    }

    if (error) {
        return (
            <Fade in timeout={500}>
                <Alert severity="error" sx={{ borderRadius: 2, border: `1px solid ${alpha('#ff5c5c', 0.3)}` }}>
                    Could not load CVE data. Please try again later.
                </Alert>
            </Fade>
        );
    }

    if (!data?.cves || data.cves.length === 0) {
        return (
            <Fade in timeout={500}>
                <EmptyStateBox>
                    <SecurityIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>No CVEs Found</Typography>
                    <Typography variant="body2" color="text.disabled">No Common Vulnerabilities and Exposures were identified in this report.</Typography>
                </EmptyStateBox>
            </Fade>
        );
    }

    return (
        <Fade in timeout={500}>
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <BugReportIcon sx={{ color: cyber.primaryLight, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: cyber.primaryLight }}>
                        <Box component="span" sx={{ color: cyber.accent.cyan, fontFamily: cyber.fontMono }}>{data.total}</Box> CVE{data.total !== 1 ? 's' : ''} Identified
                    </Typography>
                    {data.cves.some(c => c.in_cisa_kev) && (
                        <Chip
                            icon={<WarningAmberIcon />}
                            label={`${data.cves.filter(c => c.in_cisa_kev).length} in CISA KEV`}
                            size="small"
                            sx={{ bgcolor: alpha('#d32f2f', 0.18), color: '#d32f2f', fontWeight: 'bold', border: `1px solid ${alpha('#d32f2f', 0.45)}`, '& .MuiChip-icon': { color: '#d32f2f' } }}
                        />
                    )}
                    {data.cves.some(c => c.context?.exploited_in_wild) && (
                        <Chip icon={<WarningAmberIcon />} label="Active Exploitation" color="error" size="small" />
                    )}
                    {data.cves.some(c => c.cvss_v3_severity?.toUpperCase() === 'CRITICAL') && (
                        <Chip label="Contains Critical" sx={{ bgcolor: alpha('#ff5c5c', 0.15), color: '#ff5c5c', fontWeight: 'bold', border: `1px solid ${alpha('#ff5c5c', 0.3)}` }} size="small" />
                    )}
                </Box>

                <StyledTableContainer>
                    <Table aria-label="cve table">
                        <StyledTableHead>
                            <TableRow>
                                <TableCell sx={{ width: '15%' }}>CVE ID</TableCell>
                                <TableCell sx={{ width: '11%' }}>Severity</TableCell>
                                <TableCell sx={{ width: '14%' }}>
                                    <Tooltip title="CISA KEV listing and EPSS probability of exploitation"><span>Exploitation</span></Tooltip>
                                </TableCell>
                                <TableCell sx={{ width: '10%' }}>
                                    <Tooltip title="🔥 Exploited | 🔧 Patched | 💻 PoC"><span>Status</span></Tooltip>
                                </TableCell>
                                <TableCell sx={{ width: '18%' }}>Affected Products</TableCell>
                                <TableCell sx={{ width: '32%' }}>Context</TableCell>
                            </TableRow>
                        </StyledTableHead>
                        <StyledTableBody>
                            {data.cves.map((cve, idx) => (
                                <CVERow key={`${cve.cve_id}-${idx}`} cve={cve} />
                            ))}
                        </StyledTableBody>
                    </Table>
                </StyledTableContainer>
            </Box>
        </Fade>
    );
};

export default CVETable;